import axios from 'axios';
import axiosInstance from 'config/axiosConfig';
import { User } from 'types/User';
import { SubscriptionPlans } from 'util/Constants';
import { PlanTypes } from 'util/Types';
import { create } from 'zustand';

interface UserState {
    verifyUserEmail: (email: string) => Promise<string>;
    userInfo: User | null;
    fetchUser: () => Promise<User>;
    updateUserInfo: (data: Partial<User>) => Promise<User>;
    uploadUserProfileImage: (data: FormData) => Promise<number>;
    deleteUserAccount: () => Promise<number>;
    getSubscriptionPortalLink: () => Promise<string>;
    getUserPlan: () => PlanTypes;
    isPremiumUser: () => boolean;
    isProUser: () => boolean;
    isOnboardingUser: () => boolean;
    claimCompanyAccount: (data: { fullName: string; email: string; phoneNumber: string; companyId: string }) => Promise<void>;
    updateClaim: (data: { claimId: string; contactEmail: string; contactPhone: string; companyId: string }) => Promise<void>;
}

const useUserStore = create<UserState>((set, get) => ({
    verifyUserEmail: async (token: string) => {
        try {
            const { data } = await axiosInstance.get(`/user/verify-mail/?token=${token}`, {
                headers: {
                    'SKIP-AUTH': true,
                },
            });
            return data.data.message;
        } catch (error) {
            if (axios.isAxiosError(error)) throw new Error(error.response?.data.message);
        }
    },
    userInfo: null,
    fetchUser: async () => {
        try {
            const { data } = await axiosInstance.get('/user/get-user');
            set({ userInfo: data.data });
            return data.data;
        } catch (error) {
            if (axios.isAxiosError(error)) throw new Error(error.response?.data.message);
        }
    },
    updateUserInfo: async (bodyData) => {
        try {
            const { data } = await axiosInstance.patch('/user/update-user', bodyData);
            const { fullName, phone } = data.data;
            const userInfo = get().userInfo!;
            set({
                userInfo: {
                    ...userInfo,
                    fullName,
                    phone,
                },
            });

            return data.data;
        } catch (error) {
            if (axios.isAxiosError(error)) throw new Error(error.response?.data.message);
            throw new Error(error as string);
        }
    },
    uploadUserProfileImage: async (formData) => {
        try {
            const { data, status } = await axiosInstance.patch('/user/upload-profile-picture', formData);
            const user = data.data.user;
            useUserStore.setState({
                userInfo: user,
            });
            return status;
        } catch (error) {
            if (axios.isAxiosError(error)) throw new Error(error.response?.data.message);
            throw new Error(error as string);
        }
    },
    deleteUserAccount: async () => {
        try {
            const { status } = await axiosInstance.delete('/user/delete-user');
            return status;
        } catch (error) {
            if (axios.isAxiosError(error)) throw new Error(error.response?.data.message);
            throw new Error(error as string);
        }
    },
    getSubscriptionPortalLink: async () => {
        try {
            const { data } = await axiosInstance.get('/subscription/portal-link');
            return data.url;
        } catch (error) {
            if (axios.isAxiosError(error)) throw new Error(error.response?.data.message);
            throw new Error(error as string);
        }
    },
    getUserPlan: () => {
        const user = get().userInfo;
        const { priceId = '', interval = 'month' } = user?.subscriptionDetails || {};
        return SubscriptionPlans.find((p) => p.priceId[interval] === priceId) || SubscriptionPlans[0];
    },
    //TODO check if we need to correct this
    // isPremiumUser: () => get().getUserPlan().title === 'Millionaire',
    isPremiumUser: () => true,
    isProUser: () => get().getUserPlan().title === 'Pro',
    isOnboardingUser: () => {
        const user = get().userInfo;

        // If user has both accountType and subscriptionId, they are not in onboarding
        if (user?.accountType && user?.subscriptionId) {
            return false;
        }
        // Otherwise, they are in onboarding if any of these are missing
        return !user || !user.accountType || !user.subscriptionId;
    },
    claimCompanyAccount: async (data) => {
        try {
            await axiosInstance.post('/user/claim-company-account', data);
        } catch (error) {
            if (axios.isAxiosError(error)) throw new Error(error.response?.data.message);
            throw new Error(error as string);
        }
    },
    updateClaim: async (data) => {
        try {
            await axiosInstance.patch('/user/update-claim', data);
        } catch (error) {
            if (axios.isAxiosError(error)) throw new Error(error.response?.data.message);
            throw new Error(error as string);
        }
    },
}));

export default useUserStore;
